import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Button } from '@upme/ui/components/button';
import { Label } from '@upme/ui/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@upme/ui/components/select';
import { Switch } from '@upme/ui/components/switch';
import { listCitiesOptions } from '../../../features/cities/queries';
import { SourceChannelsTable } from '../../../features/source-channels/components/source-channels-table';
import {
  listSourceChannelsOptions,
  type SourceChannelListFilters,
} from '../../../features/source-channels/queries';
import { PageHeader } from '../../../shared/components/page-header';

const ANY = '__any__';

function toFilters(search: {
  cityId?: string | undefined;
  isActive?: boolean | undefined;
  kind?: 'EVENTS' | 'NEWS' | undefined;
}): SourceChannelListFilters {
  const out: SourceChannelListFilters = {};
  if (search.cityId !== undefined) out.cityId = search.cityId;
  if (search.isActive !== undefined) out.isActive = search.isActive;
  if (search.kind !== undefined) out.kind = search.kind;
  return out;
}

export const Route = createFileRoute('/_authenticated/source-channels/')({
  loaderDeps: ({ search }) => ({ cityId: search.cityId, isActive: search.isActive, kind: search.kind }),
  loader: async ({ context, deps }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(listCitiesOptions()),
      context.queryClient.ensureQueryData(listSourceChannelsOptions(toFilters(deps))),
    ]);
  },
  component: SourceChannelsIndexPage,
});

function SourceChannelsIndexPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { data: cities } = useSuspenseQuery(listCitiesOptions());
  const { data: channels } = useSuspenseQuery(listSourceChannelsOptions(toFilters(search)));

  const update = (patch: Record<string, string | boolean | undefined>) => {
    navigate({
      to: '/source-channels',
      search: () => {
        const next: Record<string, string | boolean | undefined> = { ...search, ...patch };
        const out: Record<string, string> = {};
        for (const [k, v] of Object.entries(next)) {
          if (v !== undefined && v !== '' && v !== null) out[k] = String(v);
        }
        return out;
      },
    });
  };

  return (
    <div>
      <PageHeader
        title="Источники"
        description="Telegram-каналы, из которых собираются события"
        actions={
          <Button asChild>
            <Link to="/source-channels/new">Новый канал</Link>
          </Button>
        }
      />

      <div className="border rounded-md p-4 mb-4 grid gap-4 grid-cols-1 md:grid-cols-3">
        <div>
          <Label className="text-xs">Город</Label>
          <Select
            value={search.cityId ?? ANY}
            onValueChange={(v) => update({ cityId: v === ANY ? undefined : v })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Все" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Все</SelectItem>
              {cities.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Тип</Label>
          <Select
            value={search.kind ?? ANY}
            onValueChange={(v) => update({ kind: v === ANY ? undefined : (v as 'EVENTS' | 'NEWS') })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Все" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Все</SelectItem>
              <SelectItem value="EVENTS">События</SelectItem>
              <SelectItem value="NEWS">Новости</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-2">
          <Switch
            id="only-active"
            checked={search.isActive === true}
            onCheckedChange={(v) => update({ isActive: v ? true : undefined })}
          />
          <Label htmlFor="only-active">Только активные</Label>
        </div>
      </div>

      <SourceChannelsTable channels={channels} cities={cities} />
    </div>
  );
}
